const CustomerAccount = require('@/lib/Customers/CustomerAccount');
const { apiResponseToError } = require('@/lib/Api');
const { cloneDeep } = require('@/functions/clone');

/**
 * @constructor
 *
 * @param customerAccountsResource
 * @param {Hydrator} hydrator
 */
const CustomerAccountService = function (
    customerAccountsResource,
    hydrator
) {
    this.customerAccountsResource = customerAccountsResource;
    this.hydrator = hydrator;
};

CustomerAccountService.prototype = {
    /**
     * Returns a single customer account either from the search cache or from the API.
     * Resolves with the account if the account was found.
     * Resolves with null if the account was not found.
     * Rejects if there was an error.
     *
     * @param {Number} id
     *
     * @returns {Promise<CustomerAccount>}
     */
    get(id) {
        return new Promise((resolve, reject) => {
            this.customerAccountsResource.get(
                {
                    accountID: id,
                    resolve: '*',
                },
                (response) => {
                    const account = new CustomerAccount();
                    this.hydrator.hydrate(response, account);
                    resolve(account);
                },
                (result) => reject(apiResponseToError(result))
            );
        });
    },

    /**
     * Retrieve an account from the API.
     *
     * @param {string} ref
     *
     * @returns {Promise<CustomerAccount>}
     */
    getByRef(ref) {
        return new Promise((resolve, reject) => {
            this.customerAccountsResource.query(
                {
                    q: ref,
                },
                (results) => {
                    if (results.length > 0) {
                        let customerAccount = this.hydrator.hydrate(results[0], new CustomerAccount());
                        resolve(customerAccount);
                    } else {
                        resolve(null);
                    }
                },
                () => {
                    reject();
                }
            );
        });
    },

    /**
     * A wrapper around the CustomerAccountsResource.query method, because angular-resource
     * messes with objects that are returned, even in transformResponse.
     * This takes the raw data returned by CustomerAccountsResource and returns actual
     * CustomerAccount objects.
     *
     * @param {object} params
     * @param {function} success
     * @param {function} failure
     */
    query: function (params, success, failure) {
        params.resolve = 'memberships';

        this.customerAccountsResource.query(
            params,
            (response) => {
                let models = this.hydrator.hydrateArray(response, function () {
                    return new CustomerAccount();
                });

                success(models);
            },
            failure
        );
    },

    /**
     * Wraps this.query to return a promise.
     *
     * @param {object} params
     *
     * @returns {Promise<CustomerAccount[]>}
     */
    search(params) {
        return new Promise((resolve, reject) => {
            this.query(
                params,
                resolve,
                (result) => reject(apiResponseToError(result))
            );
        });
    },

    /**
     * A wrapper around the CustomerAccountsResource.save method. Returns a CustomerAccount model
     * on success instead of the angular-resource object.
     *
     * @param {object} params
     * @param {function} success
     * @param {function} failure
     */
    save: function (params, success, failure) {
        let customerAccountParams = cloneDeep(params);
        this.customerAccountsResource.save(
            customerAccountParams,
            (response) => {
                let customerAccount = null;
                if (!response.error) {
                    customerAccount = this.hydrator.hydrate(response, new CustomerAccount());
                }

                success(response, customerAccount);
            },
            failure
        );
    },
};

module.exports = CustomerAccountService;
